import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";

export default function AttendanceHome() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [attendanceData, setAttendanceData] = useState([]);
  const token = getToken();
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [notification, setNotification] = useState(null);
  const { roles } = useContext(AuthContext);
  
  // New states for approvers modal
  const [showApproversModal, setShowApproversModal] = useState(false);
  const [approversData, setApproversData] = useState([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);

  const getRoleBasedRouteLocal = (route) => {
      return getRoleBasedRoute(route, roles);
  };

  const handleClock = (type) => {
    navigate(`camera/${type}`);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return ""; // Return empty string for null/undefined
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    fetchAttendance();
  }, [token]);

  // fetch records
  const fetchAttendance = async () => {
    try {
      const response = await axios.get("https://hrmsnew.prepstripe.com/api/attendance",  {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAttendanceData(response.data.data?.data || []);
    } catch (error) {
      console.error("Error fetching attendance records:", error);
    }
  };

  // New function to fetch approvers data
  const fetchMyApprovers = async () => {
    setLoadingApprovers(true);
    try {
      const response = await axios.get("https://hrmsnew.prepstripe.com/api/approval/my-approvers", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setApproversData(response?.data?.data?.data || []);
      setShowApproversModal(true);
    } catch (error) {
      console.error("Error fetching approvers:", error);
      setNotification({
        message: error?.response?.data?.error || "Error fetching approvers data",
        type: "error"
      });
    } finally {
      setLoadingApprovers(false);
    }
  };

  // Function to handle approval resolution
  const resolveApproval = async (approvalId, status) => {
    try {
      const response = await axios.post("https://hrmsnew.prepstripe.com/api/approval/resolve", {
        approval_id: approvalId,
        status: status
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setNotification({
        message: "Approval resolved successfully",
        type: "success"
      });
      
      // Refresh approvers data
      fetchMyApprovers();
    } catch (error) {
      console.error("Error resolving approval:", error);
      setNotification({
        message: error?.response?.data?.error || "Error resolving approval",
        type: "error"
      });
    }
  };

  // attendance filter
  const fetchAttendanceRecords = async () => {
    if (!filterFromDate) {
      setNotification({
          message: "Select from date",
          type: "error"
      })
      return;
    }  
    if (!filterToDate) {
      setNotification({
          message: "Select to date",
          type: "error"
      })
      return;
    }  
    if (!filterType) {
      setNotification({
          message: "Select filter type",
          type: "error"
      })
      return;
    }  
    try {
      const response = await axios.get(`https://hrmsnew.prepstripe.com/api/attendance/filter`, {
        params: {
          from_date: filterFromDate,
          to_date: filterToDate,
          type: filterType,  
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Filter only approved records
      const approvedRecords = (response.data?.data || []).filter((record) => {
        const checkInApproval = record.approvals.find(a => a.approval_type === "check_in");
        const checkOutApproval = record.approvals.find(a => a.approval_type === "check_out");

        return (
          (checkInApproval && checkInApproval.approval_status === "approved") ||
          (checkOutApproval && checkOutApproval.approval_status === "approved")
        );
      });

      setAttendanceData(approvedRecords);
    } catch (error) {
      console.error("Error fetching attendance:", error.response.data.error);
      setNotification({
          message: `${error?.response?.data.error || "Got an error while filtering the data"}`,
          type: "error"
      })
    }
  };

  // Function to check if the date is today
  const isToday = (isoString) => {
    const today = new Date();
    const recordDate = new Date(isoString);
    return (
      today.getDate() === recordDate.getDate() &&
      today.getMonth() === recordDate.getMonth() &&
      today.getFullYear() === recordDate.getFullYear()
    );
  };

  const latestRecord = attendanceData.length > 0 ? attendanceData[0] : null;

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 rounded-lg">
      
      {/* notification compo */}
      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      
      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4 font-poppins" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 text-md font-medium text-gray-500 md:ms-2">Attendance</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* clock in/ clock out buttons */}
      <div className="flex justify-center gap-4 p-10 font-poppins">
        <button
          onClick={() => {
            if (!user.manager) {
              setNotification({
                message: "Manager is not allocated.",
                type: "error",
              });
              return;
            }
            handleClock("Clock In");
          }}
          className="bg-green-500 hover:bg-green-600 dark:bg-black dark:border dark:border-green-500 text-white px-6 py-3 rounded-md shadow-md"
        >
          Clock In
        </button>

        <button
          onClick={() => {
            if (!user.manager) {
              setNotification({
                message: "Manager is not allocated.",
                type: "error",
              });
              return;
            }
            handleClock("Clock Out");
          }}
          className="bg-red-500 text-white px-6 py-3 rounded-md shadow-md"
        >
          Clock Out
        </button>

        {/* New View Approvers Button */}
        {/* <button
          onClick={fetchMyApprovers}
          disabled={loadingApprovers}
          className="bg-blue-500 hover:bg-blue-600 dark:bg-black dark:border dark:border-blue-500 text-white px-6 py-3 rounded-md shadow-md disabled:opacity-50"
        >
          {loadingApprovers ? "Loading..." : "View Approvers"}
        </button> */}
      </div>

      <div>
        {/* title and filter */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center font-poppins mb-4 gap-4">
          <h3 className="text-lg font-semibold dark:text-white">Attendance Records</h3>

          {/* Filter */}
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <label className=" dark:text-white" htmlFor="from_date">Start date</label>
            <input
              id="from_date"
              type="date"
              value={filterFromDate}
              required
              title="Select From date"
              onChange={(e) => setFilterFromDate(e.target.value)}
              className="p-2 dark:border-gray-700 dark:bg-black dark:text-white rounded-md border focus:outline-none text-black hover:bg-gray-100 w-full sm:w-auto md:text-md text-sm"
            />
            <label className=" dark:text-white" htmlFor="to_date">End Date</label>
            <input
              id="to_date"
              type="date"
              value={filterToDate}
              title="Select To Date"
              onChange={(e) => setFilterToDate(e.target.value)}
              className="p-2 dark:border-gray-700 dark:bg-black dark:text-white rounded-md border focus:outline-none text-black hover:bg-gray-100 w-full sm:w-auto md:text-md text-sm"
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="p-2 dark:border-gray-700 dark:bg-black dark:text-white rounded-md border focus:outline-none text-black hover:bg-gray-100 w-full sm:w-auto md:text-md text-sm"
            >
              <option value="all">All</option>
              <option value="check_in">Check In</option>
              <option value="check_out">Check Out</option>
            </select>
            <button
              onClick={fetchAttendanceRecords}
              className="border dark:bg-black dark:text-white dark:border-gray-700 hover:bg-gray-100 focus:bg-white focus:ring-1 focus:ring-gray-200 px-4 py-1.5 rounded-md w-full sm:w-auto md:text-md text-sm">
              <svg className="hidden md:block lg:block xl:block 2xl:block w-5 h-5 text-gray-500" data-slot="icon" aria-hidden="true" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
              <p className="block md:hidden lg:hidden xl:hidden 2xl:hidden">Apply Filter</p>
            </button>
          </div>
        </div>

        {/* Attendance Records Table */}
        <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
          <table className="min-w-full table-auto md:table-fixed text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
              <tr>
                {filterType !== "check_out" && (
                  <>
                  <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-black whitespace-nowrap">Check In</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-black whitespace-nowrap">Location</th>
                  </>
                )}
                {filterType !== "check_in" && (
                  <>
                  <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-black whitespace-nowrap">Check Out</th>
                  <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-black whitespace-nowrap">Location</th>
                  </>
                )}
                {filterType !== "check_out" && (
                  <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-black whitespace-nowrap">Check-In Approval</th>
                )}
                {filterType !== "check_in" && (
                  <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-black whitespace-nowrap">Check-Out Approval</th>
                )}
              </tr>
            </thead>

            <tbody>
              {attendanceData.length > 0 ? (
                attendanceData.map((record) => {
                  const checkInApproval = record.approvals.find(
                    (a) => a.approval_type === "check_in"
                  )?.approval_status || "-";

                  const checkOutApproval = record.approvals.find(
                    (a) => a.approval_type === "check_out"
                  )?.approval_status || "-";

                  return (
                  <tr key={record.id} className="dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white">
                    {filterType !== "check_out" && (
                      <>
                      <td className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">
                        {formatDateTime(record.check_in_time)}
                      </td>
                      <td className="trucate py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">
                        {record.check_out_location || "-"}
                      </td>
                      </>
                    )}
                    {filterType !== "check_in" && (
                      <>
                      <td className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">
                        {record.check_out_time ? formatDateTime(record.check_out_time) : ""}
                      </td>
                      <td className="trucate py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">
                        {record.check_in_location || "-"}
                      </td>
                      </>
                    )}
                    
                    {filterType !== "check_out" && (
                      <td className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">
                        {checkInApproval}
                      </td>
                    )}
                    {filterType !== "check_in" && (
                      <td className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">
                        {checkOutApproval}
                      </td>
                    )}
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center dark:text-white text-gray-500 font-poppins p-2">No record found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approvers Modal */}
      {showApproversModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowApproversModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
              <h2 className="text-xl font-semibold dark:text-white font-poppins">My Approvers</h2>
              <button
                onClick={() => setShowApproversModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {approversData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        {/* <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">Request ID</th> */}
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">Approval Type</th>
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">Primary Approver</th>
                        <th className="py-3 px-4 text-left text-xs md:text-sm font-medium text-black dark:text-white">Secondary Approver</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {approversData.slice(0, 1).map((approval) => (
                        <tr key={approval.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          {/* <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            #{approval.id}
                          </td> */}
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            {approval.approval_module_id || "-"}
                          </td>
                          {/* <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                              {approval.approval_level_id || "-"}
                            </span>
                          </td> */}
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            <span className="px-2 py-1 rounded-full text-md ">
                              {approval.primary_approver_id || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs md:text-sm text-gray-700 dark:text-gray-300">
                            <span className="px-2 py-1 rounded-full text-md ">
                              {approval.secondary_approver_id || "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400 font-poppins">No approvers found.</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t dark:border-gray-700">
              <button
                onClick={() => setShowApproversModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-poppins"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
}